import { getAgent, searchPlace } from '@/api/common'
import useMessage from '@/hooks/useMessage'
import useStationListStore from '@/stores/stationList'
import { Button, Col, Form, Input, Modal, Row, Select, Timeline, TimelineItemProps, Tooltip } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Agent, Area, TOUTariffPrice } from '@/type/stationType'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { debounce } from 'lodash'
import { currency, TARIFF_TYPE } from '@/utils/const'
import UpdateImg from './UpdateImg'
import TariffItem from './TariffItem'
import useValidator from '@/hooks/useValidator'
import useGlobalConfigStore from '@/stores/globalConfig'
import { addStation, setEnterprise, setStationTariff } from '@/api/powerStation'

const CreateSite: React.FC<{ open: boolean; onClose: () => void; onAdd: () => void }> = ({ open, onClose, onAdd }) => {
  const { t } = useTranslation()
  const { message } = useMessage()
  const { validateNumbersOrDecimal, validateTariffItem } = useValidator()
  const device_type = useStationListStore(state => state.device_type)
  const station_type = useStationListStore(state => state.station_type)
  const mapOk = useGlobalConfigStore(state => state.mapOk)
  const [timelineItems, setTimelineItems] = useState<TimelineItemProps[]>([])
  const basicInfoRef = useRef<any>(null)
  const deviceRef = useRef<any>(null)
  const tariffRef = useRef<any>(null)
  const authorisationRef = useRef<any>(null)

  useEffect(() => {
    setTimelineItems([
      {
        children: (
          <div
            className='pointer'
            onClick={() => {
              basicInfoRef.current?.scrollIntoView()
            }}>
            {t('site.basicInfo')}
          </div>
        ),
        dot: <div className='stepCircle'>1</div>,
      },
      {
        children: (
          <div
            className='pointer'
            onClick={() => {
              deviceRef.current?.scrollIntoView()
            }}>
            {t('site.addDevice')}
          </div>
        ),
        dot: <div className='stepCircle'>2</div>,
      },
      {
        children: (
          <div
            className='pointer'
            onClick={() => {
              tariffRef.current?.scrollIntoView()
            }}>
            {t('site.setTariff')}
          </div>
        ),
        dot: <div className='stepCircle'>3</div>,
      },
      {
        children: (
          <div
            className='pointer'
            onClick={() => {
              authorisationRef.current?.scrollIntoView()
            }}>
            {t('site.authorisation')}
          </div>
        ),
        dot: <div className='stepCircle'>4</div>,
      },
    ])
  }, [t])
  const [priceUnit, setPriceUnit] = useState('AUD')

  const [form] = Form.useForm()
  const initialValues = {
    basicInfo: {
      station_name: '',
      station_type: undefined,
      address: undefined,
      postal_code: '',
      station_image: [] as string[],
      installed_capacity: '',
      rated_capacity: '',
      nmi: '',
      nmi_reg: 0,
      area: undefined,
      agent: '',
    },
    device: { type: undefined, sn: '', name: '' },
    tariff: {
      unit: 'AUD',
      feedin: {
        type: TARIFF_TYPE.FIXED,
        price: '0.08',
      },
      consumption: {
        type: TARIFF_TYPE.FIXED,
        price: '0.37',
      },
    },
    enterprise_code: '',
  }
  const [stateArray, setStateArray] = useState<Area[]>([])
  const [agentArray, setAgentArray] = useState<Agent[]>([])
  const [chosenAgent, setChosenAgent] = useState('')

  const handleAreaChange = (value: string) => {
    const target = stateArray.find(item => item.code_type === value)
    if (target) {
      setAgentArray(target.agent || [])
    }
  }

  const getAndSetAgent = async () => {
    let res: any
    try {
      res = await getAgent()
    } catch (error: any) {
      console.log(error)
      message.error(error.message || error.msg || error)
      return
    }
    if (res.code === 1001) {
      res.data.area.forEach((item: Area) => {
        item.agent = res.data.agent.filter((agent: Agent) => agent.code_type === item.code_type)
      })
      setStateArray(res.data.area)
    } else {
      console.log(res)
      message.error(t('site.getAgentFailed'))
      return
    }
  }

  const [addressOptions, setAddressOptions] = useState<string[]>([])
  const handleSearch = debounce(async (value: string) => {
    let res: any
    try {
      res = await searchPlace(value)
    } catch (error: any) {
      console.log(error)
      message.error(error.message || error.msg || error)
      return
    }
    // console.log(res)
    setAddressOptions(res.data.map((item: any) => item.FULL_ADDRESS))
  }, 200)
  const handleAddressChange = (value: string) => {
    const array = value.split(' ')
    form.setFieldValue(['basicInfo', 'postal_code'], array[array.length - 1])
  }

  useEffect(() => {
    if (open) {
      if (mapOk) {
        form.resetFields()
        getAndSetAgent()
        setStateArray([])
        setAgentArray([])
        setChosenAgent('')
        setAddressOptions([])
      } else {
        message.error(t('common.mapUnready'))
        onClose()
      }
    }
  }, [open])

  const geocodeAddress = (address: string) => {
    return new Promise<{ latitude: number; longitude: number }>((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder()
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK') {
          const location = (results as google.maps.GeocoderResult[])[0].geometry.location
          resolve({ latitude: location.lat(), longitude: location.lng() })
        } else {
          reject(t('common.mapError') + status)
        }
      })
    })
  }

  const doAddSite = async () => {
    let values
    try {
      values = await form.validateFields()
    } catch (error: any) {
      console.log(error)
      return
    }
    console.log(values)
    //  获取坐标
    let location: { latitude: number; longitude: number }
    try {
      location = await geocodeAddress(values.address)
    } catch (error: any) {
      console.log(error)
      message.error(error)
      return
    }
    //  创建场站
    let res: any
    try {
      res = await addStation({ ...values.basicInfo, ...location, device: values.device })
    } catch (error: any) {
      console.log(error)
      message.error(error.message || error.msg || error)
      return
    }
    if (res.code != 1001) {
      message.error(res.msg)
      return
    } else {
      //  设置电价
      const station_id = res.data.station_id
      let feedinPrice: string | TOUTariffPrice[]
      if (values.tariff.feedin.type === TARIFF_TYPE.FIXED) {
        feedinPrice = values.tariff.feedin.price
      } else {
        feedinPrice = values.tariff.feedin.price.map((item: TOUTariffPrice) => item)
        ;(feedinPrice as TOUTariffPrice[]).forEach(item => {
          item.data = item.data.map(item2 => ({
            startTime: item2.startTime,
            endTime: item2.endTime,
            value: item2.value,
          }))
        })
      }
      let consumptionPrice: string | TOUTariffPrice[]
      if (values.tariff.consumption.type === TARIFF_TYPE.FIXED) {
        consumptionPrice = values.tariff.consumption.price
      } else {
        consumptionPrice = values.tariff.consumption.price.map((item: TOUTariffPrice) => item)
        ;(consumptionPrice as TOUTariffPrice[]).forEach(item => {
          item.data = item.data.map(item2 => ({
            startTime: item2.startTime,
            endTime: item2.endTime,
            value: item2.value,
          }))
        })
      }
      try {
        res = await setStationTariff({
          station_id,
          consumptionPrice,
          feedinPrice,
          unit: values.tariff.unit,
          consumptionType: values.tariff.consumption.type,
          feedinType: values.tariff.feedin.type,
        })
      } catch (error: any) {
        console.log(error)
        message.error(error.message || error.msg || error)
        return
      }
      if (res.code != 1001) {
        message.error(res.msg)
        return
      } else {
        if (values.tariff.enterprise_code && values.tariff.enterprise_code !== '') {
          //  设置安装商
          try {
            res = await setEnterprise({ station_id, enterprise_code: values.enterprise_code })
          } catch (error: any) {
            console.log(error)
            message.error(error.message || error.msg || error)
          }
          if (res?.code != 1001) {
            message.error(res.msg)
          }
        }
        message.success(t('site.addSuccess'))
        onAdd()
      }
    }
  }

  return (
    <Modal
      className='create-site-modal'
      style={{ maxWidth: '100vw', top: 0, paddingBottom: 0 }}
      open={open}
      closable={false}
      footer={null}
      width='100vw'
      destroyOnClose>
      <div style={{ height: '100vh' }} className='create-site-content'>
        <div className='app-header'>
          <div className='app-header-left'>
            <img className='logo' src='/assets/web/logo.svg' />
          </div>
          <div className='create-site-header-right'>
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button type='primary' onClick={doAddSite}>
              {t('common.save')}
            </Button>
          </div>
        </div>
        <div className='create-site-body'>
          <div className='create-site-left'>
            <Timeline items={timelineItems} />
          </div>
          <div className='create-site-right'>
            <Form form={form} layout='vertical' initialValues={initialValues}>
              <div className='subtitle' ref={basicInfoRef}>
                {t('site.basicInfo')}
              </div>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label={t('site.siteType')}
                    name={['basicInfo', 'station_type']}
                    rules={[{ required: true, message: t('site.requiredFields') }]}>
                    <Select
                      options={station_type.map(item => ({ label: item.code_name, value: item.code_id }))}
                      placeholder={t('common.pleaseSelect')}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t('site.siteName')}
                    name={['basicInfo', 'station_name']}
                    rules={[{ required: true, message: t('site.requiredFields') }]}>
                    <Input placeholder={t('common.pleaseEnter')} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label={t('site.installedCapacity')}
                    name={['basicInfo', 'installed_capacity']}
                    rules={[
                      { required: true, message: t('site.requiredFields') },
                      { validator: validateNumbersOrDecimal },
                    ]}>
                    <Input placeholder={t('common.pleaseEnter')} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t('site.bess')}
                    name={['basicInfo', 'rated_capacity']}
                    rules={[
                      { required: true, message: t('site.requiredFields') },
                      { validator: validateNumbersOrDecimal },
                    ]}>
                    <Input placeholder={t('common.pleaseEnter')} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item label={t('site.sitePhoto')} name={['basicInfo', 'station_image']}>
                    <UpdateImg />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t('site.seachForAddress')}
                    name={['basicInfo', 'address']}
                    rules={[{ required: true, message: t('site.requiredFields') }]}>
                    <Select
                      placeholder={t('common.pleaseEnter')}
                      showSearch
                      defaultActiveFirstOption={false}
                      suffixIcon={null}
                      filterOption={false}
                      onSearch={handleSearch}
                      onChange={handleAddressChange}
                      options={addressOptions.map(item => ({ label: item, value: item }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label={t('site.postCode')}
                    name={['basicInfo', 'postal_code']}
                    rules={[{ required: true, message: t('site.requiredFields') }]}>
                    <Input placeholder={t('common.pleaseEnter')} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label={t('site.nmi')} name={['basicInfo', 'nmi']}>
                    <Input placeholder={t('common.pleaseEnter')} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label={t('site.state')}
                    name={['basicInfo', 'area']}
                    rules={[{ required: true, message: t('site.requiredFields') }]}>
                    <Select
                      options={stateArray.map(item => ({ label: item.type_name, value: item.code_type }))}
                      placeholder={t('common.pleaseSelect')}
                      onChange={handleAreaChange}
                    />
                  </Form.Item>
                </Col>
                {agentArray.length > 0 && (
                  <Col span={12}>
                    <Form.Item
                      label={t('site.agent')}
                      name={['basicInfo', 'agent']}
                      rules={[{ required: true, message: t('site.requiredFields') }]}>
                      <Select
                        options={agentArray.map(item => ({ label: item.code_name, value: item.code_id }))}
                        placeholder={t('common.pleaseSelect')}
                        onChange={(_value, option: any) => setChosenAgent(option.label)}
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>
              {chosenAgent !== '' && (
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item label={t('site.nmireg') + chosenAgent} name={['basicInfo', 'nmi_reg']}>
                      <Select
                        options={[
                          { label: 'Yes', value: 1 },
                          { label: 'No', value: 0 },
                        ]}
                        placeholder={t('common.pleaseSelect')}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}
              <div className='create-site-separator'></div>
              <div className='subtitle' ref={deviceRef}>
                {t('site.addDevice')}
              </div>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label={t('device.deviceType')}
                    name={['device', 'type']}
                    rules={[{ required: true, message: t('site.requiredFields') }]}>
                    <Select
                      options={device_type}
                      fieldNames={{ label: 'code_name', value: 'code_id' }}
                      placeholder={t('common.pleaseSelect')}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t('device.deviceName')}
                    name={['device', 'name']}
                    rules={[{ required: true, message: t('site.requiredFields') }]}>
                    <Input placeholder={t('common.pleaseEnter')} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item
                    label={t('device.sn')}
                    name={['device', 'sn']}
                    rules={[{ required: true, message: t('site.requiredFields') }]}>
                    <Input placeholder={t('common.pleaseEnter')} />
                  </Form.Item>
                </Col>
              </Row>
              <div className='create-site-separator'></div>
              <div className='subtitle' ref={tariffRef}>
                {t('site.setTariff')}
                <Tooltip overlayStyle={{ maxWidth: '500px' }} title={t('site.tariffTip')} placement='right'>
                  <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                </Tooltip>
              </div>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label={t('site.unit')}
                    name={['tariff', 'unit']}
                    rules={[{ required: true, message: t('site.requiredFields') }]}>
                    <Select
                      placeholder={t('common.pleaseSelect')}
                      options={currency.map(item => ({ label: item, value: item }))}
                      onChange={setPriceUnit}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item
                    label={t('site.feedinTariff')}
                    name={['tariff', 'feedin']}
                    rules={[{ required: true, message: t('site.requiredFields') }, { validator: validateTariffItem }]}>
                    <TariffItem suffix={priceUnit} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item
                    label={t('site.consumptionTariff')}
                    name={['tariff', 'consumption']}
                    rules={[{ required: true, message: t('site.requiredFields') }, { validator: validateTariffItem }]}>
                    <TariffItem suffix={priceUnit} />
                  </Form.Item>
                </Col>
              </Row>
              <div className='create-site-separator'></div>
              <div className='subtitle' ref={authorisationRef}>
                {t('site.authorisation')}
              </div>
              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item
                    label={t('site.enterpriseCode')}
                    name='enterprise_code'
                    tooltip={t('site.setEnterpriseTips')}>
                    <Input placeholder={t('common.pleaseEnter')} />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default CreateSite
